import React, { useRef, useState } from "react";
import {
  alpha,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
  useTheme,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { Download, Info, TriangleAlert, Upload, X } from "lucide-react";
import { onErrorMessage } from "@utils/utilsApi";
import { openToast } from "@utils/toastStore";

export interface UploadModalBanner {
  title: string;
  text: string;
  downloadLabel: string;
  onDownload: () => void | Promise<unknown>;
  secondaryDownloadLabel?: string;
  onSecondaryDownload?: () => void | Promise<unknown>;
}

interface UploadModalCstProps {
  open: boolean;
  onClose: () => void;
  title: string;
  accept?: string;
  banner?: UploadModalBanner;
  onUpload: (file: File) => Promise<any>;
  onUploaded?: () => void;
  successMessage?: string;
}

interface DiscardsFile {
  data: any;
  filename: string;
  type: string;
}

const UploadModalCst: React.FC<UploadModalCstProps> = ({
  open,
  onClose,
  title,
  accept,
  banner,
  onUpload,
  onUploaded,
  successMessage,
}) => {
  const { t } = useTranslation("upload-modal");
  const theme = useTheme();

  const inputRef = useRef<HTMLInputElement>(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [isSecondaryDownloading, setIsSecondaryDownloading] = useState(false);
  const [discards, setDiscards] = useState<DiscardsFile | null>(null);

  const handleDownload = async () => {
    if (!banner) return;
    setIsDownloading(true);
    try {
      await banner.onDownload();
    } catch {
      openToast({
        severity: "error",
        message: String(t("download-error")),
      });
    } finally {
      setIsDownloading(false);
    }
  };

  const handleSecondaryDownload = async () => {
    if (!banner?.onSecondaryDownload) return;
    setIsSecondaryDownloading(true);
    try {
      await banner.onSecondaryDownload();
    } catch {
      openToast({
        severity: "error",
        message: String(t("download-error")),
      });
    } finally {
      setIsSecondaryDownloading(false);
    }
  };

  const handleFile = async (file: File) => {
    setIsUploading(true);
    try {
      const response: any = await onUpload(file);
      onUploaded?.();

      const explicitDiscards: DiscardsFile | undefined = response?.discards;
      const explicitSuccessMessage: string | undefined =
        response?.successMessage;

      if (explicitDiscards) {
        setDiscards(explicitDiscards);
        openToast({
          severity: "warning",
          message: String(t("with-discards")),
        });
        return;
      }

      const contentDisposition: string | undefined =
        response?.headers?.["content-disposition"];

      if (contentDisposition && contentDisposition.includes("filename")) {
        setDiscards({
          data: response.data,
          filename: contentDisposition
            .split("filename=")[1]
            ?.replace(/["']/g, "")
            .trim(),
          type: response?.headers?.["content-type"] || "text/csv",
        });
        openToast({
          severity: "warning",
          message: String(t("with-discards")),
        });
      } else {
        openToast({
          severity: "success",
          message:
            explicitSuccessMessage || successMessage || String(t("success")),
        });
        onClose();
      }
    } catch (e: any) {
      openToast({
        severity: "error",
        message: onErrorMessage(e) || String(t("error")),
      });
    } finally {
      setIsUploading(false);
    }
  };

  const handleDownloadDiscards = () => {
    if (!discards) return;
    const bom = new Uint8Array([0xef, 0xbb, 0xbf]);
    const blob = new Blob([bom, discards.data], { type: discards.type });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = discards.filename || "discards.csv";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const onPickFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) handleFile(file);
    e.target.value = "";
  };

  const onDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragOver(false);
    if (isUploading) return;
    const file = e.dataTransfer.files?.[0];
    if (file) handleFile(file);
  };

  return (
    <Dialog
      open={open}
      onClose={isUploading ? undefined : onClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{
        sx: {
          backgroundColor: theme.palette.background.default,
          borderRadius: "16px",
          border: `1px solid ${theme.palette.divider}`,
        },
      }}
    >
      <DialogTitle sx={{ padding: "20px 24px 8px" }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Typography
            sx={{
              fontSize: "20px",
              fontWeight: 700,
              color: theme.palette.text.primary,
            }}
          >
            {title}
          </Typography>
          <IconButton
            onClick={onClose}
            aria-label="close"
            disabled={isUploading}
            sx={{ color: theme.palette.text.primary }}
          >
            <X size={20} />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent
        sx={{
          padding: "12px 24px 24px",
          display: "flex",
          flexDirection: "column",
          gap: 2,
        }}
      >
        {banner && (
          <Box
            sx={{
              display: "flex",
              gap: 1.5,
              padding: "16px",
              borderRadius: "12px",
              backgroundColor: alpha(theme.palette.info.main, 0.12),
              border: `1px solid ${alpha(theme.palette.info.main, 0.3)}`,
            }}
          >
            <Info
              size={18}
              color={theme.palette.info.main}
              style={{ flexShrink: 0, marginTop: 2 }}
            />
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 1,
                flex: 1,
              }}
            >
              <Typography
                sx={{
                  fontSize: "13px",
                  fontWeight: 700,
                  color: theme.palette.text.primary,
                }}
              >
                {banner.title}
              </Typography>
              <Typography
                sx={{ fontSize: "12px", color: theme.palette.text.tertiary }}
              >
                {banner.text}
              </Typography>
              <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap" }}>
                <Button
                  variant="outlined"
                  startIcon={
                    isDownloading ? (
                      <CircularProgress size={14} />
                    ) : (
                      <Download size={16} />
                    )
                  }
                  onClick={handleDownload}
                  disabled={isDownloading}
                  sx={{
                    textTransform: "none",
                    fontWeight: 700,
                    borderRadius: "8px",
                    borderColor: theme.palette.divider,
                    color: theme.palette.text.primary,
                  }}
                >
                  {banner.downloadLabel}
                </Button>
                {banner.onSecondaryDownload &&
                  banner.secondaryDownloadLabel && (
                    <Button
                      variant="outlined"
                      startIcon={
                        isSecondaryDownloading ? (
                          <CircularProgress size={14} />
                        ) : (
                          <Download size={16} />
                        )
                      }
                      onClick={handleSecondaryDownload}
                      disabled={isSecondaryDownloading}
                      sx={{
                        textTransform: "none",
                        fontWeight: 700,
                        borderRadius: "8px",
                        borderColor: theme.palette.divider,
                        color: theme.palette.text.primary,
                      }}
                    >
                      {banner.secondaryDownloadLabel}
                    </Button>
                  )}
              </Box>
            </Box>
          </Box>
        )}

        <Box
          role="button"
          onClick={() => !isUploading && inputRef.current?.click()}
          onDragOver={(e) => {
            e.preventDefault();
            setIsDragOver(true);
          }}
          onDragLeave={() => setIsDragOver(false)}
          onDrop={onDrop}
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: 1,
            padding: "40px 24px",
            borderRadius: "12px",
            cursor: isUploading ? "default" : "pointer",
            border: `1.5px dashed ${
              isDragOver ? theme.palette.primary.main : theme.palette.divider
            }`,
            backgroundColor: isDragOver
              ? alpha(theme.palette.primary.main, 0.06)
              : "transparent",
            transition: "border-color .15s, background-color .15s",
          }}
        >
          {isUploading ? (
            <>
              <CircularProgress size={28} />
              <Typography
                sx={{ fontSize: "13px", color: theme.palette.text.tertiary }}
              >
                {t("uploading")}
              </Typography>
            </>
          ) : (
            <>
              <Upload size={26} color={theme.palette.primary.main} />
              <Typography
                sx={{
                  fontSize: "14px",
                  fontWeight: 700,
                  color: theme.palette.primary.main,
                }}
              >
                {t("dropzone-title")}
              </Typography>
              <Typography
                sx={{
                  fontSize: "13px",
                  color: theme.palette.text.tertiary,
                  textAlign: "center",
                }}
              >
                {t("dropzone-text")}
              </Typography>
            </>
          )}
          <input
            ref={inputRef}
            type="file"
            accept={accept}
            hidden
            onChange={onPickFile}
          />
        </Box>

        {discards && (
          <Box
            sx={{
              display: "flex",
              gap: 1.5,
              padding: "16px",
              borderRadius: "12px",
              backgroundColor: alpha(theme.palette.warning.main, 0.12),
              border: `1px solid ${alpha(theme.palette.warning.main, 0.3)}`,
            }}
          >
            <TriangleAlert
              size={18}
              color={theme.palette.warning.main}
              style={{ flexShrink: 0, marginTop: 2 }}
            />
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 1,
                flex: 1,
              }}
            >
              <Typography
                sx={{
                  fontSize: "13px",
                  fontWeight: 700,
                  color: theme.palette.text.primary,
                }}
              >
                {t("discards-title")}
              </Typography>
              <Typography
                sx={{ fontSize: "12px", color: theme.palette.text.tertiary }}
              >
                {t("discards-text")}
              </Typography>
              <Box>
                <Button
                  variant="outlined"
                  startIcon={<Download size={16} />}
                  onClick={handleDownloadDiscards}
                  sx={{
                    textTransform: "none",
                    fontWeight: 700,
                    borderRadius: "8px",
                    borderColor: theme.palette.divider,
                    color: theme.palette.text.primary,
                  }}
                >
                  {t("discards-download")}
                </Button>
              </Box>
            </Box>
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default UploadModalCst;
